import React, { useEffect, useState } from 'react';
import { FileCheck, CheckCircle, Lock } from 'lucide-react';
import toast from 'react-hot-toast';
import api from '../../api/axios';

// Staff toggle for the sick note (AU) of one of their own sick reports.
// Can be set and removed until the end of the shift's month (Europe/Berlin);
// after that read-only (the server decides: record.au_editable).

const lastDayOfMonth = (dateStr) => {
  const [y, m] = dateStr.split('-').map(Number);
  const d = new Date(Date.UTC(y, m, 0));
  return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', timeZone: 'UTC' });
};

const boxStyle = {
  padding: '12px 14px', borderRadius: 'var(--radius)', fontSize: '0.88rem', lineHeight: 1.5,
  background: 'var(--primary-xlight)', border: '1px solid var(--primary)',
};

export default function SickNoteToggle({ record, onChange }) {
  const [confirm, setConfirm] = useState(null); // 'set' | 'remove' | null
  const [saving, setSaving] = useState(false);
  useEffect(() => { setConfirm(null); }, [record?.id]);
  if (!record) return null;

  const hasAu = !!record.au_submitted;
  const editable = record.au_editable !== false;

  const save = async (au) => {
    setSaving(true);
    try {
      const res = await api.put(`/sickness/${record.id}/au`, { au });
      toast.success(au ? 'Sick note noted.' : 'Sick note removed.');
      setConfirm(null);
      onChange?.(res.data);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to save the sick note.');
    } finally {
      setSaving(false);
    }
  };

  const until = record.date ? ` You can change this until ${lastDayOfMonth(record.date)}.` : '';

  if (!editable) {
    return (
      <div style={{ ...boxStyle, background: 'var(--bg, #F8FAFC)', borderColor: 'var(--border)' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontWeight: 600 }}>
          {hasAu ? <CheckCircle size={15} style={{ color: 'var(--success)' }} /> : <Lock size={15} style={{ color: 'var(--text-light)' }} />}
          {hasAu ? 'Sick note (AU) noted' : 'No sick note (AU)'}
        </div>
        <div style={{ fontSize: '0.8rem', color: 'var(--text-light)' }}>Ask an admin to change this.</div>
      </div>
    );
  }

  if (confirm === 'set') {
    return (
      <div role="alertdialog" aria-label="Confirm sick note" style={boxStyle}>
        <p style={{ margin: '0 0 10px' }}>
          Only confirm this if you have a doctor&apos;s sick note (AU) for this day. It will be marked in the payroll
          export. Confirm?
        </p>
        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
          <button className="btn btn-primary btn-sm" style={{ minHeight: '40px' }} onClick={() => save(true)} disabled={saving}>
            {saving ? 'Saving…' : 'Confirm'}
          </button>
          <button className="btn btn-secondary btn-sm" style={{ minHeight: '40px' }} onClick={() => setConfirm(null)} disabled={saving}>
            Cancel
          </button>
        </div>
      </div>
    );
  }

  if (confirm === 'remove') {
    return (
      <div role="alertdialog" aria-label="Remove sick note" style={boxStyle}>
        <p style={{ margin: '0 0 10px' }}>Remove the sick note (AU) for this day? It will count as sick without AU.</p>
        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
          <button className="btn btn-danger btn-sm" style={{ minHeight: '40px' }} onClick={() => save(false)} disabled={saving}>
            {saving ? 'Saving…' : 'Remove'}
          </button>
          <button className="btn btn-secondary btn-sm" style={{ minHeight: '40px' }} onClick={() => setConfirm(null)} disabled={saving}>
            Cancel
          </button>
        </div>
      </div>
    );
  }

  if (hasAu) {
    return (
      <div style={boxStyle}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px', flexWrap: 'wrap' }}>
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', fontWeight: 600 }}>
            <CheckCircle size={15} style={{ color: 'var(--success)' }} /> Sick note (AU) noted
          </span>
          <button className="btn btn-secondary btn-sm" style={{ minHeight: '36px' }} onClick={() => setConfirm('remove')}>
            Remove
          </button>
        </div>
        {until && <div style={{ fontSize: '0.78rem', color: 'var(--text-light)', marginTop: '4px' }}>{until.trim()}</div>}
      </div>
    );
  }

  return (
    <button
      className="btn btn-secondary"
      style={{ justifyContent: 'flex-start', textAlign: 'left', width: '100%' }}
      onClick={() => setConfirm('set')}
      title={until.trim()}
    >
      <FileCheck size={15} />
      I have a sick note (Krankschreibung / AU)
    </button>
  );
}
